import { HeadersOptionsType } from "@/types/ModuleOptionsType";
import { ParentsType } from "../../../wp/getParentPages";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Post } from "@/wp/types/client";
import { ImageType } from "@/types/baseTypes";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { useState, useEffect } from "react";
import { getVideoData } from "@/getData/getVideoData";
import { useRouter } from "next/router";
import { HeaderVideoPlayer } from "../HeaderVideoPlayer/HeaderVideoPlayer";
import { Headline } from "@/components/Headline/Headline";
import { MediaItemResponsive } from "@/components/MediaItem/MediaItemResponsive";
import { BlogPostHeader } from "../BlogPostHeader/BlogPostHeader";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { Button } from "@/components/Button/Button";
import { passACFLink } from "@/utils/passACFLink";
import { CategoryOverviewPages } from "@/getData/getIndexedPostGridData";
import { CustomLink } from "@/components/CustomLink";

export interface PostHeaderProps {
  post: Post<"blog">;
  headerOptions?: HeadersOptionsType["header_posts"];
  parents: ParentsType;
  fallbackImg?: ImageType | false;
  blogCategories: CategoryOverviewPages[];
  isNobleElements: boolean;
}

export const PostHeader = ({
  post,
  headerOptions,
  parents,
  fallbackImg,
  blogCategories,
  isNobleElements,
}: PostHeaderProps) => {
  const header = post.acf.header_post;
  const {
    overline = "",
    subline = "",
    media,
    background_color_class,
    headline_text_align_class,
    content,
    link,
    link_extended,
    seo_settings: { breadcrumb_title },
  } = header;

  const {
    show_overline_subline_headers: show_overline_subline,
    splitheader,
    breadcrumb_position,
    button_type,
    show_extended_link,
  } = headerOptions ?? {};

  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
  } = splitheader ?? {};
  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );
  /* jscpd:ignore-end */
  const date = new Date(post.date_gmt).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);
  const router = useRouter();
  useEffect(() => {
    if (!media || media.type !== "video") return;
    const videoId = "video" in media ? media.video?.[0] : undefined;
    async function fetchVideoData() {
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        fields: "acf.header_videos,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });
      if (videoResponse) {
        setVideoData(videoResponse);
      }
    }
    fetchVideoData();

    //eslint-disable-next-line
  }, []);

  const breadcrumbsJSX = (
    <div className="nav-oriented-breadcrumbs">
      <Breadcrumbs
        parents={parents}
        // post={post}
        breadcrumbPositon={
          breadcrumb_position
            ? breadcrumb_position
            : "is-justify-content-flex-start"
        }
        breadcrumbTitle={breadcrumb_title}
      />
    </div>
  );
  const mediaJSX =
    media && media.type === "image" ? (
      <MediaItemResponsive
        image_desktop={
          media.image_desktop
            ? media.image_desktop
            : fallbackImg
              ? fallbackImg
              : false
        }
        image_tablet={media.image_tablet}
        image_mobile={media.image_mobile}
        image_fullhd={media.image_fullhd}
        // mediaFormat={imageheaderMediaFormat}
        sizes="(max-width: 1023px) 100vw, (max-width: 1599px) 50vw, 1024px"
        priority={true}
        mediaFormat={
          header.type === "splitheader" ? splitheaderMediaFormat : undefined
        }
        className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
        figcaption={media.show_caption && media.caption ? media.caption : ""}
        caption_position={media.caption_position}
      />
    ) : media && media.type === "video" && videoData ? (
      <HeaderVideoPlayer
        video={videoData.acf.header_videos}
        autoplay={media.autoplay}
        singlePageUrl={videoData.link}
      />
    ) : (
      <></>
    );
  const contentJSX = (
    <>
      <span className={"category-date-overline"}>
        {blogCategories.map((cat, i) =>
          !isNobleElements && cat.link ? (
            <CustomLink key={cat.id} href={cat.link} target="_self">
              {cat.name.toUpperCase()}
              {i < blogCategories.length - 1 ? " | " : ""}
            </CustomLink>
          ) : (
            <span key={cat.id}>
              {cat.name.toUpperCase()}
              {i < blogCategories.length - 1 ? " | " : ""}
            </span>
          ),
        )}
        {blogCategories.length > 0 ? " | " : ""}
        {date}
      </span>
      <Headline
        headline={header.title}
        headline_html_tag="h1-posts"
        extraClass={`is-flex is-flex-direction-column ${headline_text_align_class}`}
        overline={showOverline ? overline : ""}
        subline={showSubline ? subline : ""}
        disableAnimation
      />
      {mediaJSX}
      {content && <ContentTag extraClass="intro-text" content={content} />}
      {show_extended_link && link_extended ? (
        <ExtendedLink
          extendedLink={link_extended}
          btnType={button_type ?? "primary"}
        />
      ) : (
        link &&
        link.url && (
          <Button
            type={button_type ?? "primary"}
            {...passACFLink({ link }, "link")}
          >
            {link.title}
          </Button>
        )
      )}
    </>
  );

  return (
    <BlogPostHeader
      breadcrumbsJSX={breadcrumbsJSX}
      contentJSX={contentJSX}
      sectionClassName={`${background_color_class}`}
    />
  );
};
